import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { usePostLoginRedirect, useVerifyEmailTokenMutation } from "@/hooks/use-auth";
import { getApiError } from "@/lib/api/errors";
import { Button } from "@/components/ui/button";

const TOKEN_PATTERN = /^[a-f0-9]{64}$/;

type VerifyError = "expired" | "invalid" | "rate_limited" | "generic";

const VERIFY_ERROR_COPY: Record<
  VerifyError,
  { heading: string; description: string }
> = {
  expired: {
    heading: "Your secure link has expired",
    description:
      "Sign-in links expire after a short time for your security. Sign in again to get a fresh link.",
  },
  invalid: {
    heading: "This link isn't valid",
    description:
      "This sign-in link is incomplete or was never issued. Sign in again to get a new one.",
  },
  rate_limited: {
    heading: "Too many attempts",
    description:
      "We couldn't check your link just now. Wait a few minutes, then open it again.",
  },
  generic: {
    heading: "This link didn't work",
    description:
      "Something went wrong while verifying your link. Sign in again to get a fresh one.",
  },
};

function classifyVerifyError(err: unknown): VerifyError {
  // 410: the link existed but is past its TTL (expired).
  // 400: the token was never issued or is malformed (invalid).
  // 429: rate-limited; the link itself may still be good.
  const { status } = getApiError(err);
  if (status === 410) return "expired";
  if (status === 400) return "invalid";
  if (status === 429) return "rate_limited";
  return "generic";
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const verify = useVerifyEmailTokenMutation();
  const postLoginRedirect = usePostLoginRedirect();
  const started = useRef(false);
  const [error, setError] = useState<VerifyError | null>(null);

  useEffect(() => {
    // Guards against StrictMode's double-invoke firing the verify request twice.
    if (started.current) return;
    started.current = true;

    if (!TOKEN_PATTERN.test(token)) {
      setError("invalid");
      return;
    }

    (async () => {
      let session;
      try {
        session = await verify.mutateAsync({ token });
      } catch (err) {
        setError(classifyVerifyError(err));
        return;
      }

      // The token verified, so a failure here is a post-login glitch, not a bad link.
      try {
        await postLoginRedirect(session.token);
      } catch {
        setError("generic");
      }
    })();
  }, []);

  if (error) {
    const { heading, description } = VERIFY_ERROR_COPY[error];

    return (
      <div>
        <h1 className="text-4xl font-semibold tracking-tight">{heading}</h1>
        <p className="mt-2 text-muted-foreground">{description}</p>
        <Button asChild className="mt-6 w-full cursor-pointer text-base">
          <Link to="/login">Try logging in again</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      <h1 className="mt-4 text-4xl font-semibold tracking-tight">
        Signing you in…
      </h1>
      <p className="mt-2 text-muted-foreground">
        Hold on while we verify your link.
      </p>
    </div>
  );
}
